import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react'
import { flushSync } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  buildRoutePath,
  getWorkspaceTabTitle,
  parseRouteTo,
} from '@/lib/route-title'
import { randomId } from '@/lib/random-id'
import { findTabForDestination } from '@/lib/workspace-tab-route-match'

export type WorkspaceTab = {
  id: string
  pathname: string
  search: string
  title: string
}

type TabState = { tabs: WorkspaceTab[]; activeTabId: string }

type Stored = TabState

const STORAGE_KEY = 'sd2_workspace_tabs_v1'

function loadStored(): Stored | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as Stored
    if (!Array.isArray(p.tabs) || typeof p.activeTabId !== 'string') return null
    return p
  } catch {
    return null
  }
}

function saveStored(s: TabState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  } catch {
    /* ignore */
  }
}

function newTab(pathname: string, search: string): WorkspaceTab {
  const id = randomId('tab')
  return {
    id,
    pathname,
    search: search || '',
    title: getWorkspaceTabTitle(pathname, search),
  }
}

/** Восстановление из sessionStorage с учётом текущего URL (прямая ссылка, обновление страницы). */
function reconcileOnLoad(
  loc: { pathname: string; search: string },
  stored: Stored | null,
): TabState {
  if (!stored?.tabs?.length) {
    const t = newTab(loc.pathname, loc.search)
    return { tabs: [t], activeTabId: t.id }
  }
  const match = findTabForDestination(stored.tabs, loc.pathname, loc.search || '')
  if (match) {
    return {
      activeTabId: match.id,
      tabs: stored.tabs.map((t) =>
        t.id === match.id
          ? {
              ...t,
              pathname: loc.pathname,
              search: loc.search || '',
              title: getWorkspaceTabTitle(loc.pathname, loc.search),
            }
          : t,
      ),
    }
  }
  const curActive =
    stored.tabs.find((t) => t.id === stored.activeTabId) ?? stored.tabs[0]
  return {
    activeTabId: curActive.id,
    tabs: stored.tabs.map((t) =>
      t.id === curActive.id
        ? {
            ...t,
            pathname: loc.pathname,
            search: loc.search || '',
            title: getWorkspaceTabTitle(loc.pathname, loc.search),
          }
        : t,
    ),
  }
}

export type OpenWorkspaceTabOptions = {
  /** false — добавить вкладку в фоне, не переключаться и не менять URL (по умолчанию true) */
  activate?: boolean
}

type WorkspaceTabsContextValue = {
  tabs: WorkspaceTab[]
  activeTabId: string
  pendingPath: string | null
  selectTab: (id: string) => void
  closeTab: (id: string) => void
  /** Всегда новая вкладка (дубликат URL допустим) */
  openNewTab: (to: string, options?: OpenWorkspaceTabOptions) => void
  /** Переход из меню: новая вкладка или активация уже открытой с тем же логическим маршрутом */
  openOrSelectTab: (to: string) => void
  openInBrowserTab: (tabId?: string) => void
  /** Подпись активной вкладки без смены URL (например карточка обращения после загрузки). */
  updateActiveTabTitle: (title: string) => void
}

const WorkspaceTabsContext = createContext<WorkspaceTabsContextValue | null>(null)

export function WorkspaceTabsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const locInitRef = useRef<{ pathname: string; search: string } | null>(null)
  if (locInitRef.current === null) {
    locInitRef.current = { pathname: location.pathname, search: location.search }
  }

  const [state, setState] = useState<TabState>(() =>
    reconcileOnLoad(locInitRef.current!, loadStored()),
  )
  const [pendingPath, setPendingPath] = useState<string | null>(null)
  const { tabs, activeTabId } = state

  const setActiveTabId = useCallback((id: string) => {
    setState((s) => ({ ...s, activeTabId: id }))
  }, [])

  /** Навигация внутри приложения обновляет путь и заголовок активной вкладки */
  useEffect(() => {
    const currentPath = buildRoutePath(location.pathname, location.search || '')
    setPendingPath((pending) => (pending === currentPath ? null : pending))
    setState((s) => ({
      ...s,
      tabs: s.tabs.map((t) =>
        t.id === s.activeTabId
          ? {
              ...t,
              pathname: location.pathname,
              search: location.search || '',
              title: getWorkspaceTabTitle(location.pathname, location.search),
            }
          : t,
      ),
    }))
  }, [location.pathname, location.search])

  useEffect(() => {
    saveStored(state)
  }, [state])

  const selectTab = useCallback(
    (id: string) => {
      const tab = tabs.find((t) => t.id === id)
      if (!tab) return
      const path = buildRoutePath(tab.pathname, tab.search)
      const currentPath = buildRoutePath(location.pathname, location.search || '')
      flushSync(() => {
        setActiveTabId(id)
        setPendingPath(path === currentPath ? null : path)
      })
      navigate(path)
    },
    [tabs, navigate, setActiveTabId, location.pathname, location.search],
  )

  const closeTab = useCallback(
    (id: string) => {
      if (tabs.length <= 1) return
      const idx = tabs.findIndex((t) => t.id === id)
      if (idx === -1) return
      const nextTabs = tabs.filter((t) => t.id !== id)
      const nextActive =
        id === activeTabId
          ? nextTabs[Math.max(0, idx - 1)] ?? nextTabs[0]
          : tabs.find((t) => t.id === activeTabId)!
      setState({
        tabs: nextTabs,
        activeTabId: nextActive.id,
      })
      if (id === activeTabId) {
        const path = buildRoutePath(nextActive.pathname, nextActive.search)
        const currentPath = buildRoutePath(location.pathname, location.search || '')
        setPendingPath(path === currentPath ? null : path)
        navigate(path)
      }
    },
    [tabs, activeTabId, navigate, location.pathname, location.search],
  )

  const openNewTab = useCallback(
    (to: string, options?: OpenWorkspaceTabOptions) => {
      const activate = options?.activate !== false
      const { pathname, search } = parseRouteTo(to)
      const tab = newTab(pathname, search)
      const path = buildRoutePath(pathname, search)
      const currentPath = buildRoutePath(location.pathname, location.search || '')
      flushSync(() => {
        setState((s) => ({
          tabs: [...s.tabs, tab],
          activeTabId: activate ? tab.id : s.activeTabId,
        }))
        if (activate) setPendingPath(path === currentPath ? null : path)
      })
      if (activate) navigate(to)
    },
    [navigate, location.pathname, location.search],
  )

  const openOrSelectTab = useCallback(
    (to: string) => {
      const { pathname, search } = parseRouteTo(to)
      const path = buildRoutePath(pathname, search)
      const currentPath = buildRoutePath(location.pathname, location.search || '')
      flushSync(() => {
        setState((s) => {
          const existing = findTabForDestination(s.tabs, pathname, search)
          if (existing) {
            return { ...s, activeTabId: existing.id }
          }
          const tab = newTab(pathname, search)
          return { tabs: [...s.tabs, tab], activeTabId: tab.id }
        })
        setPendingPath(path === currentPath ? null : path)
      })
      navigate(path)
    },
    [navigate, location.pathname, location.search],
  )

  const openInBrowserTab = useCallback(
    (tabId?: string) => {
      const id = tabId ?? activeTabId
      const tab = tabs.find((t) => t.id === id)
      if (!tab) return
      const url = `${window.location.origin}${buildRoutePath(tab.pathname, tab.search)}`
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    [tabs, activeTabId],
  )

  const updateActiveTabTitle = useCallback((title: string) => {
    const t = title.trim().slice(0, 120) || 'Страница'
    setState((s) => ({
      ...s,
      tabs: s.tabs.map((tab) =>
        tab.id === s.activeTabId ? { ...tab, title: t } : tab,
      ),
    }))
  }, [])

  const value = useMemo(
    () => ({
      tabs,
      activeTabId,
      pendingPath,
      selectTab,
      closeTab,
      openNewTab,
      openOrSelectTab,
      openInBrowserTab,
      updateActiveTabTitle,
    }),
    [
      tabs,
      activeTabId,
      pendingPath,
      selectTab,
      closeTab,
      openNewTab,
      openOrSelectTab,
      openInBrowserTab,
      updateActiveTabTitle,
    ],
  )

  return (
    <WorkspaceTabsContext.Provider value={value}>{children}</WorkspaceTabsContext.Provider>
  )
}

export function useWorkspaceTabs() {
  const ctx = useContext(WorkspaceTabsContext)
  if (!ctx) throw new Error('useWorkspaceTabs outside WorkspaceTabsProvider')
  return ctx
}

export function useTabAwareNavHandlers(to: string) {
  const { openNewTab, openOrSelectTab } = useWorkspaceTabs()
  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        openNewTab(to)
        return
      }
      e.preventDefault()
      openOrSelectTab(to)
    },
    [to, openNewTab, openOrSelectTab],
  )
  const onAuxClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (e.button === 1) {
        e.preventDefault()
        openNewTab(to)
      }
    },
    [to, openNewTab],
  )
  return { onClick, onAuxClick, href: to }
}
